import React, { useEffect, useState, useRef, createElement } from 'react';
import './LightRays.css';
interface LightRaysProps {
  raysOrigin?: 'top-left' | 'top-right' | 'left' | 'right' | 'bottom-left' | 'bottom-center' | 'bottom-right' | 'top-center';
  raysColor?: string;
  raysSpeed?: number;
  lightSpread?: number;
  rayLength?: number;
  pulsating?: boolean;
  fadeDistance?: number;
  saturation?: number;
  followMouse?: boolean;
  mouseInfluence?: number;
  noiseAmount?: number;
  distortion?: number;
  className?: string;
}
const DEFAULT_COLOR = '#ffffff';
const hexToRgb = (hex: string): [number, number, number] => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return m ? [parseInt(m[1], 16) / 255, parseInt(m[2], 16) / 255, parseInt(m[3], 16) / 255] : [1, 1, 1];
};
const getAnchorAndDir = (origin: string, w: number, h: number) => {
  const outside = 0.2;
  switch (origin) {
    case 'top-left':
      return {
        anchor: [0, -outside * h],
        dir: [0, 1]
      };
    case 'top-right':
      return {
        anchor: [w, -outside * h],
        dir: [0, 1]
      };
    case 'left':
      return {
        anchor: [-outside * w, 0.5 * h],
        dir: [1, 0]
      };
    case 'right':
      return {
        anchor: [(1 + outside) * w, 0.5 * h],
        dir: [-1, 0]
      };
    case 'bottom-left':
      return {
        anchor: [0, (1 + outside) * h],
        dir: [0, -1]
      };
    case 'bottom-center':
      return {
        anchor: [0.5 * w, (1 + outside) * h],
        dir: [0, -1]
      };
    case 'bottom-right':
      return {
        anchor: [w, (1 + outside) * h],
        dir: [0, -1]
      };
    default:
      return {
        anchor: [0.5 * w, -outside * h],
        dir: [0, 1]
      };
  }
};
const VERT = `
attribute vec2 position;
varying vec2 vUv;
void main() {
  vUv = position * 0.5 + 0.5;
  gl_Position = vec4(position, 0.0, 1.0);
}`;
const FRAG = `precision highp float;
uniform float iTime;
uniform vec2  iResolution;
uniform vec2  rayPos;
uniform vec2  rayDir;
uniform vec3  raysColor;
uniform float raysSpeed;
uniform float lightSpread;
uniform float rayLength;
uniform float pulsating;
uniform float fadeDistance;
uniform float saturation;
uniform vec2  mousePos;
uniform float mouseInfluence;
uniform float noiseAmount;
uniform float distortion;
varying vec2 vUv;

float noise(vec2 st) {
  return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123);
}

float rayStrength(vec2 raySource, vec2 rayRefDirection, vec2 coord,
                  float seedA, float seedB, float speed) {
  vec2 sourceToCoord = coord - raySource;
  vec2 dirNorm = normalize(sourceToCoord);
  float cosAngle = dot(dirNorm, rayRefDirection);
  float distortedAngle = cosAngle + distortion * sin(iTime * 2.0 + length(sourceToCoord) * 0.01) * 0.2;

  float spreadFactor = pow(max(distortedAngle, 0.0), 1.0 / max(lightSpread, 0.001));
  float distance = length(sourceToCoord);
  float maxDistance = iResolution.x * rayLength;
  float lengthFalloff = clamp((maxDistance - distance) / maxDistance, 0.0, 1.0);

  float fadeFalloff = clamp((iResolution.x * fadeDistance - distance) / (iResolution.x * fadeDistance), 0.5, 1.0);
  float pulse = pulsating > 0.5 ? (0.8 + 0.2 * sin(iTime * speed * 3.0)) : 1.0;
  float baseStrength = clamp(
    (0.45 + 0.15 * sin(distortedAngle * seedA + iTime * speed)) +
    (0.3  + 0.2  * cos(-distortedAngle * seedB + iTime * speed)),
    0.0, 1.0
  );
  return baseStrength * lengthFalloff * fadeFalloff * spreadFactor * pulse;
}

void mainImage(out vec4 fragColor, in vec2 fragCoord) {
  vec2 coord = vec2(fragCoord.x, iResolution.y - fragCoord.y);

  vec2 finalRayDir = rayDir;
  if (mouseInfluence > 0.0) {
    vec2 mouseScreenPos = mousePos * iResolution.xy;
    vec2 mouseDirection = normalize(mouseScreenPos - rayPos);
    finalRayDir = normalize(mix(rayDir, mouseDirection, mouseInfluence));
  }

  vec4 rays1 = vec4(1.0) * rayStrength(rayPos, finalRayDir, coord, 36.2214, 21.11349, 1.5 * raysSpeed);
  vec4 rays2 = vec4(1.0) * rayStrength(rayPos, finalRayDir, coord, 22.3991, 18.0234,  1.1 * raysSpeed);
  fragColor = rays1 * 0.5 + rays2 * 0.4;

  if (noiseAmount > 0.0) {
    float n = noise(coord * 0.01 + iTime * 0.1);
    fragColor.rgb *= (1.0 - noiseAmount + noiseAmount * n);
  }

  float brightness = 1.0 - (coord.y / iResolution.y);
  fragColor.x *= 0.1 + brightness * 0.8;
  fragColor.y *= 0.3 + brightness * 0.6;
  fragColor.z *= 0.5 + brightness * 0.5;

  if (saturation != 1.0) {
    float gray = dot(fragColor.rgb, vec3(0.299, 0.587, 0.114));
    fragColor.rgb = mix(vec3(gray), fragColor.rgb, saturation);
  }

  fragColor.rgb *= raysColor;
}

void main() {
  vec4 color;
  mainImage(color, gl_FragCoord.xy);
  gl_FragColor = color;
}`;
// ─── Raw WebGL helpers (replaces ogl) ────────────────────────────────────────
function createShader(gl: WebGLRenderingContext, type: number, src: string): WebGLShader {
  const shader = gl.createShader(type)!;
  gl.shaderSource(shader, src);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    throw new Error(gl.getShaderInfoLog(shader) ?? 'Shader compile error');
  }
  return shader;
}
function createProgram(gl: WebGLRenderingContext, vert: string, frag: string): WebGLProgram {
  const program = gl.createProgram()!;
  gl.attachShader(program, createShader(gl, gl.VERTEX_SHADER, vert));
  gl.attachShader(program, createShader(gl, gl.FRAGMENT_SHADER, frag));
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    throw new Error(gl.getProgramInfoLog(program) ?? 'Program link error');
  }
  return program;
}
// Full-screen triangle (covers NDC [-1,1] with a single triangle)
const TRIANGLE_POSITIONS = new Float32Array([-1, -1, 3, -1, -1, 3]);
// ─────────────────────────────────────────────────────────────────────────────
const LightRays: React.FC<LightRaysProps> = ({
  raysOrigin = 'top-center',
  raysColor = DEFAULT_COLOR,
  raysSpeed = 1,
  lightSpread = 1,
  rayLength = 2,
  pulsating = false,
  fadeDistance = 1.0,
  saturation = 1.0,
  followMouse = true,
  mouseInfluence = 0.1,
  noiseAmount = 0.0,
  distortion = 0.0,
  className = ''
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const glRef = useRef<WebGLRenderingContext | null>(null);
  const programRef = useRef<WebGLProgram | null>(null);
  const uniformsRef = useRef<Record<string, WebGLUniformLocation | null>>({});
  const animationIdRef = useRef<number | null>(null);
  const mouseRef = useRef({
    x: 0.5,
    y: 0.5
  });
  const smoothMouseRef = useRef({
    x: 0.5,
    y: 0.5
  });
  const cleanupRef = useRef<(() => void) | null>(null);
  const [isVisible, setIsVisible] = useState(true);
  // Uniform value cache so we can update without re-init
  const uValRef = useRef({
    raysColor: hexToRgb(raysColor),
    raysSpeed,
    lightSpread,
    rayLength,
    pulsating: pulsating ? 1.0 : 0.0,
    fadeDistance,
    saturation,
    mouseInfluence,
    noiseAmount,
    distortion,
    rayPos: [0, 0] as [number, number],
    rayDir: [0, 1] as [number, number]
  });
  // ── IntersectionObserver ──────────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new IntersectionObserver(([e]) => setIsVisible(e.isIntersecting), {
      threshold: 0
    });
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);
  // ── WebGL init ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isVisible || !containerRef.current) return;
    // Tear down previous instance
    cleanupRef.current?.();
    cleanupRef.current = null;
    const container = containerRef.current;
    const canvas = document.createElement('canvas');
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    while (container.firstChild) container.removeChild(container.firstChild);
    container.appendChild(canvas);
    const gl = canvas.getContext('webgl', {
      alpha: true,
      premultipliedAlpha: false
    }) as WebGLRenderingContext | null;
    if (!gl) {
      console.warn('WebGL not supported');
      return;
    }
    glRef.current = gl;
    let program: WebGLProgram;
    try {
      program = createProgram(gl, VERT, FRAG);
    } catch (e) {
      console.error('LightRays shader error:', e);
      return;
    }
    programRef.current = program;
    // Position buffer (full-screen triangle)
    const buf = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, TRIANGLE_POSITIONS, gl.STATIC_DRAW);
    const posLoc = gl.getAttribLocation(program, 'position');
    gl.enableVertexAttribArray(posLoc);
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);
    // Cache uniform locations
    const uNames = ['iTime', 'iResolution', 'rayPos', 'rayDir', 'raysColor', 'raysSpeed', 'lightSpread', 'rayLength', 'pulsating', 'fadeDistance', 'saturation', 'mousePos', 'mouseInfluence', 'noiseAmount', 'distortion'];
    const locs: Record<string, WebGLUniformLocation | null> = {};
    uNames.forEach(n => {
      locs[n] = gl.getUniformLocation(program, n);
    });
    uniformsRef.current = locs;
    const dpr = Math.min(window.devicePixelRatio, 2);
    const resize = () => {
      if (!container) return;
      const w = container.clientWidth;
      const h = container.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      gl.viewport(0, 0, canvas.width, canvas.height);
      const {
        anchor,
        dir
      } = getAnchorAndDir(raysOrigin, canvas.width, canvas.height);
      uValRef.current.rayPos = anchor as [number, number];
      uValRef.current.rayDir = dir as [number, number];
    };
    resize();
    window.addEventListener('resize', resize);
    const loop = (t: number) => {
      if (!glRef.current || !programRef.current) return;
      const u = uValRef.current;
      const lc = uniformsRef.current;
      if (followMouse && mouseInfluence > 0) {
        const s = 0.92;
        smoothMouseRef.current.x = smoothMouseRef.current.x * s + mouseRef.current.x * (1 - s);
        smoothMouseRef.current.y = smoothMouseRef.current.y * s + mouseRef.current.y * (1 - s);
      }
      gl.useProgram(program);
      gl.uniform1f(lc['iTime'], t * 0.001);
      gl.uniform2f(lc['iResolution'], canvas.width, canvas.height);
      gl.uniform2fv(lc['rayPos'], u.rayPos);
      gl.uniform2fv(lc['rayDir'], u.rayDir);
      gl.uniform3fv(lc['raysColor'], u.raysColor);
      gl.uniform1f(lc['raysSpeed'], u.raysSpeed);
      gl.uniform1f(lc['lightSpread'], u.lightSpread);
      gl.uniform1f(lc['rayLength'], u.rayLength);
      gl.uniform1f(lc['pulsating'], u.pulsating);
      gl.uniform1f(lc['fadeDistance'], u.fadeDistance);
      gl.uniform1f(lc['saturation'], u.saturation);
      gl.uniform2f(lc['mousePos'], smoothMouseRef.current.x, smoothMouseRef.current.y);
      gl.uniform1f(lc['mouseInfluence'], u.mouseInfluence);
      gl.uniform1f(lc['noiseAmount'], u.noiseAmount);
      gl.uniform1f(lc['distortion'], u.distortion);
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      animationIdRef.current = requestAnimationFrame(loop);
    };
    animationIdRef.current = requestAnimationFrame(loop);
    cleanupRef.current = () => {
      if (animationIdRef.current) cancelAnimationFrame(animationIdRef.current);
      window.removeEventListener('resize', resize);
      try {
        const ext = gl.getExtension('WEBGL_lose_context');
        ext?.loseContext();
        if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
      } catch (_) {}
      glRef.current = null;
      programRef.current = null;
    };
    return () => {
      cleanupRef.current?.();
      cleanupRef.current = null;
    };
  }, [isVisible, raysOrigin]); // re-init only when visibility or origin changes
  // ── Prop updates (no re-init needed) ─────────────────────────────────────
  useEffect(() => {
    const u = uValRef.current;
    u.raysColor = hexToRgb(raysColor);
    u.raysSpeed = raysSpeed;
    u.lightSpread = lightSpread;
    u.rayLength = rayLength;
    u.pulsating = pulsating ? 1.0 : 0.0;
    u.fadeDistance = fadeDistance;
    u.saturation = saturation;
    u.mouseInfluence = mouseInfluence;
    u.noiseAmount = noiseAmount;
    u.distortion = distortion;
    // Recompute ray position if origin changed (handled by resize in init, but keep in sync)
    if (glRef.current && containerRef.current) {
      const dpr = Math.min(window.devicePixelRatio, 2);
      const w = containerRef.current.clientWidth * dpr;
      const h = containerRef.current.clientHeight * dpr;
      const {
        anchor,
        dir
      } = getAnchorAndDir(raysOrigin, w, h);
      u.rayPos = anchor as [number, number];
      u.rayDir = dir as [number, number];
    }
  }, [raysColor, raysSpeed, lightSpread, raysOrigin, rayLength, pulsating, fadeDistance, saturation, mouseInfluence, noiseAmount, distortion]);
  // ── Mouse tracking ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!followMouse) return;
    const handle = (e: MouseEvent) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      mouseRef.current = {
        x: (e.clientX - rect.left) / rect.width,
        y: (e.clientY - rect.top) / rect.height
      };
    };
    window.addEventListener('mousemove', handle);
    return () => window.removeEventListener('mousemove', handle);
  }, [followMouse]);
  return <div ref={containerRef} className={`light-rays-container ${className}`.trim()} data-id="element-63" />;
};
export default LightRays;